import { useEffect, useRef } from 'react';
import { Keyboard } from '@capacitor/keyboard';
import { PluginListenerHandle } from '@capacitor/core';
import { isNativePlatform } from '@/lib/platform';
import { logger } from '@/lib/logger';

export const useKeyboardAvoidance = () => {
  const originalScrollPos = useRef(0);
  const activeInput = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isNativePlatform()) return;

    let showListener: PluginListenerHandle | undefined;
    let hideListener: PluginListenerHandle | undefined;

    const setupListeners = async () => {
      // Track focused input/textarea elements
      const handleFocus = (e: FocusEvent) => {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          activeInput.current = target;
          logger.debug('useKeyboardAvoidance', 'Input focused', target.tagName);
        }
      };

      // Keyboard show - add class to hide footer
      showListener = await Keyboard.addListener('keyboardWillShow', (info) => {
        document.documentElement.classList.add('kb-open');
        
        if (!activeInput.current) return;
        
        // Store original scroll position for restoration
        originalScrollPos.current = window.scrollY;
        
        const inputRect = activeInput.current.getBoundingClientRect();
        const keyboardHeight = info.keyboardHeight;
        const viewportHeight = window.innerHeight;
        const safeZone = viewportHeight - keyboardHeight - 20; // 20px padding above keyboard
        
        logger.debug('useKeyboardAvoidance', 'Keyboard showing', {
          keyboardHeight,
          inputBottom: inputRect.bottom,
          safeZone,
          needsScroll: inputRect.bottom > safeZone
        });
        
        // Only scroll if input would be covered by keyboard
        if (inputRect.bottom > safeZone) {
          const scrollAmount = inputRect.bottom - safeZone + originalScrollPos.current;
          window.scrollTo({
            top: scrollAmount,
            behavior: 'smooth'
          });
          logger.debug('useKeyboardAvoidance', 'Scrolling to', scrollAmount);
        }
      });

      // Keyboard hide - remove class and restore scroll
      hideListener = await Keyboard.addListener('keyboardWillHide', () => {
        document.documentElement.classList.remove('kb-open');
        logger.debug('useKeyboardAvoidance', 'Keyboard hiding, restoring scroll to', originalScrollPos.current);
        window.scrollTo({
          top: originalScrollPos.current,
          behavior: 'smooth'
        });
        activeInput.current = null;
      });

      // Use capture phase to catch focus events early
      document.addEventListener('focusin', handleFocus, true);
    };

    setupListeners();

    return () => {
      if (showListener) showListener.remove();
      if (hideListener) hideListener.remove();
    };
  }, []);
};
